import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import { API_URL } from "../../Config/secrets";

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: null,
  tab: 'Brands',
  coverageData: null,
  brandsCoverage: null,
  retailersCoverage: null,
};

const slice = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },

    changeTab(state, action) {
      state.tab = action.payload;
    },

    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    getCoverageDataSuccess(state, action) {
      state.error = null;
      state.isLoading = false;
      state.coverageData = action.payload;
    },

    getBrandsCoverageSuccess(state, action) {
      state.error = null;
      state.isLoading = false;
      state.brandsCoverage = action.payload;
    },

    getRetailersCoverageSuccess(state, action) {
      state.error = null;
      state.isLoading = false;
      state.retailersCoverage = action.payload;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  startLoading,
  changeTab,
  hasError,
  getCoverageDataSuccess,
  getBrandsCoverageSuccess,
  getRetailersCoverageSuccess
} = slice.actions;

// ----------------------------------------------------------------------

export function getCoverageData(tab, location, subTab, createdDateRange, updatedDateRange) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const response = await axios.post(
        `${API_URL}/analytics/get-coverage`,
        {
          tab: tab === 'Brands' ? 'brands' : 'retailer',
          ...(location && { location }),
          ...(subTab && { subTab }),
          ...(createdDateRange?.startDate && { createdStartDate: createdDateRange.startDate }),
          ...(createdDateRange?.endDate && { createdEndDate: createdDateRange.endDate }),
          ...(updatedDateRange?.startDate && { updatedStartDate: updatedDateRange.startDate }),
          ...(updatedDateRange?.endDate && { updatedEndDate: updatedDateRange.endDate })
        }
      );

      if (response.data.statusCode === 200) {
        dispatch(getCoverageDataSuccess(response.data.data));

        // Store in specific state based on tab
        if (tab === 'Brands') {
          dispatch(getBrandsCoverageSuccess(response.data.data));
        } else {
          dispatch(getRetailersCoverageSuccess(response.data.data));
        }

        return response.data.data;
      } else {
        dispatch(hasError(response.data.message));
        toast.error(response.data.message || "Failed to fetch coverage data");
      }
    } catch (error) {
      console.error("Error fetching coverage data:", error);
      dispatch(hasError(error.message));
      toast.error(error.response?.data?.message || "Failed to fetch coverage data");
    }
  };
}

// Data-quality funnel for the dashboard; returns the data instead of storing it.
// `selections` ({ [stage key]: bucket names }) overrides which buckets move forward.
export function getDataQualityFunnel(tab, selections = {}) {
  return async () => {
    try {
      const response = await axios.post(`${API_URL}/analytics/get-data-quality-funnel`, {
        tab: tab === 'Brands' ? 'brands' : 'retailer',
        selections,
      });
      return response.data.data;
    } catch (error) {
      console.error("Error fetching data quality funnel:", error);
      toast.error(error.response?.data?.message || "Failed to fetch data quality funnel");
    }
  };
}

// ---- Data Pipeline page ----
const pipelineTab = (entity) => (entity === 'retailers' ? 'retailer' : 'brands');

export function getDataPipeline(entity) {
  return async () => {
    try {
      const response = await axios.post(`${API_URL}/analytics/data-pipeline`, { tab: pipelineTab(entity) });
      return response.data.data;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load the data pipeline");
    }
  };
}

export function getDataPipelineRecords(entity, query) {
  return async () => {
    try {
      const response = await axios.post(`${API_URL}/analytics/data-pipeline/records`, { tab: pipelineTab(entity), ...query });
      return response.data.data;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load records");
    }
  };
}

// CSV of the records matching `query` (or the hand-picked `ids`).
export function exportDataPipeline(entity, query) {
  return async () => {
    try {
      const response = await axios.post(`${API_URL}/analytics/data-pipeline/export`, { tab: pipelineTab(entity), ...query }, { responseType: "blob" });
      const href = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", `${entity}_pipeline_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(href);
      return true;
    } catch (error) {
      let message;
      try { message = JSON.parse(await error?.response?.data?.text())?.message; } catch {}
      toast.error(message || "Export failed");
      return false;
    }
  };
}

// Which records an AI job covers: { count, records: { query } | { ids } }.
export function getDataPipelineJob(entity, scope) {
  return async () => {
    try {
      const response = await axios.post(`${API_URL}/analytics/data-pipeline/job`, { tab: pipelineTab(entity), ...scope });
      return response.data.data;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to prepare the job");
    }
  };
}
